import React, { useState, useEffect, useRef } from "react";
import styles from "../../../styles/MelodyQuiz.module.css";
import { Note } from "../../../soundLibrary";
import { PlaybackOptions } from "../../../components/PlaybackOptionsPanel";
import LearnMoreBanner from "../../../components/LearnMoreBanner";
import { ROUTES } from "../../../config/routes";
import { ScaleType, formatScaleDegreeSequence } from "../../../utils/musicTheory";

export type MelodyQuizOptions = Omit<MelodyQuizProps, "playbackOptions" | "currentQuestion" | "setCurrentQuestion" | "questionCount" | "setQuestionCount" | "attemptedQuestions" | "setAttemptedQuestions" | "score" | "setScore" | "timeLeft" | "setTimeLeft" | "feedback" | "setFeedback" | "generateQuestion" | "quizEnded" | "setQuizEnded" | "resetQuiz" | "playReferenceNote" | "stopAll">;

export interface MelodyQuizProps {
    selectedScales: ScaleType[];
    selectedDegrees: number[];
    melodyLength: number;
    mode: "questions" | "time" | "endless";
    questionLimit?: number;
    timeLimitSeconds?: number;
    difficulty?: number;  // 1-3, where higher = more similar incorrect answers
    playbackOptions: PlaybackOptions;
    referenceNote?: Note;
    currentQuestion?: {
        melody: Note[];
        correctSequence: number[];
        questionOptions: number[][];
        referenceNote: Note;
        scaleType: ScaleType;
    } | null;
    setCurrentQuestion?: (question: any) => void;
    questionCount?: number;
    setQuestionCount?: (count: number) => void;
    attemptedQuestions?: number;
    setAttemptedQuestions?: (count: number) => void;
    score?: number;
    setScore?: (score: number) => void;
    timeLeft?: number;
    setTimeLeft?: (time: number) => void;
    feedback?: { message: string; color: string } | null;
    setFeedback?: (feedback: any) => void;
    generateQuestion?: () => void;
    quizEnded?: boolean;
    setQuizEnded?: (ended: boolean) => void;
    resetQuiz?: () => void;
    playReferenceNote?: () => void;
    stopAll?: () => void;
}

export default function MelodyQuiz({
    selectedScales,
    selectedDegrees,
    melodyLength,
    mode,
    questionLimit,
    timeLimitSeconds,
    difficulty = 1,
    playbackOptions,
    currentQuestion,
    setCurrentQuestion,
    questionCount = 0,
    setQuestionCount,
    attemptedQuestions = 0,
    setAttemptedQuestions,
    score = 0,
    setScore,
    timeLeft = 0,
    setTimeLeft,
    feedback,
    setFeedback,
    generateQuestion,
    quizEnded = false,
    setQuizEnded,
    resetQuiz,
    playReferenceNote,
    stopAll,
}: MelodyQuizProps) {
    const [disabledOptions, setDisabledOptions] = useState<number[][]>([]);
    const [correctAnswer, setCorrectAnswer] = useState<number[] | null>(null);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);

    // Clear any pending timeouts when quiz ends
    useEffect(() => {
        if (quizEnded && timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, [quizEnded]);

    // Reset correctAnswer and disabledOptions when question changes
    useEffect(() => {
        setCorrectAnswer(null);
        setDisabledOptions([]);
    }, [currentQuestion]);

    // Auto-end quiz when timer runs out
    useEffect(() => {
        if (mode === "time" && timeLeft === 0 && !quizEnded && setQuizEnded) {
            if (stopAll) stopAll(); // Stop any currently playing melody
            setQuizEnded(true);
        }
    }, [mode, timeLeft, quizEnded, setQuizEnded, stopAll]);

    const handleSequenceSelect = (selectedSequence: number[]) => {
        if (!currentQuestion || !setFeedback || !setQuestionCount || !setCurrentQuestion) return;

        // Skip if already disabled
        if (disabledOptions.some((seq) => arraysEqual(seq, selectedSequence))) {
            return;
        }

        // Increment attempted questions only on the first answer attempt
        if (disabledOptions.length === 0 && setAttemptedQuestions) {
            setAttemptedQuestions(attemptedQuestions + 1);
        }

        const isCorrect = arraysEqual(selectedSequence, currentQuestion.correctSequence);

        if (isCorrect) {
            if (disabledOptions.length === 0 && setScore) {
                setScore(score + 1);
            }

            // Increment question count immediately when answer is selected
            if (setQuestionCount) {
                setQuestionCount(questionCount + 1);
            }

            // ✅ Correct feedback
            setFeedback({ message: "✓ Correct!", color: "#22c55e" });
            setCorrectAnswer(selectedSequence);

            // Check if quiz should end after this question
            const shouldEndQuiz = (mode === "questions" && questionLimit && (questionCount + 1) >= questionLimit) || 
                                 quizEnded;

            // Advance after 2s, but only if quiz isn't ending
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            timeoutRef.current = setTimeout(() => {
                setFeedback(null);
                setDisabledOptions([]);
                setCorrectAnswer(null);
                if (!shouldEndQuiz && generateQuestion && !quizEnded) {
                    // Generate new question
                    generateQuestion();
                } else if (shouldEndQuiz && !quizEnded) {
                    // End the quiz when the final question is answered
                    if (stopAll) stopAll(); // Stop any currently playing melody
                    if (setQuizEnded) {
                        setQuizEnded(true);
                    }
                }
                timeoutRef.current = null;
            }, 2000);
        } else {
            // ❌ Wrong feedback
            setDisabledOptions((prev) => [...prev, selectedSequence]);
            setFeedback({ message: "Try again!", color: "#ef4444" });
        }
    };

    if (!currentQuestion) {
        return <div className={styles.loadingMessage}>Loading question...</div>;
    }

    if (quizEnded || (mode === "questions" && questionLimit && questionCount >= questionLimit)) {
        const percentage = attemptedQuestions > 0 ? Math.round((score / attemptedQuestions) * 100) : 0;
        const needsMorePractice = percentage < 70;
        return (
            <div ref={containerRef} className={styles.quizCompletedPanel}>
                <div className={styles.completionIcon}>🎉</div>
                <h2 className={styles.completionTitle}>Quiz Complete!</h2>
                <div className={styles.scoreDisplay}>
                    <div className={styles.scoreNumber}>{score}/{attemptedQuestions}</div>
                    <div className={styles.scorePercentage}>{percentage}%</div>
                </div>
                <div className={styles.completionMessage}>
                    You answered {score} of {attemptedQuestions} questions correctly
                </div>
                {resetQuiz && (
                    <button onClick={resetQuiz} className={styles.newQuizButton}>
                        New Quiz
                    </button>
                )}
                <div className={styles.completionLearnMore}>
                    <LearnMoreBanner
                        to={ROUTES.LEARN.MELODIES}
                        variant="completion"
                        title={needsMorePractice ? "Want to improve?" : "Keep learning"}
                        description={needsMorePractice
                            ? "Learn tips for recognizing scale degrees"
                            : "Explore melodic patterns and techniques"
                        }
                    />
                </div>
            </div>
        );
    }

    return (
        <div ref={containerRef} className={styles.quizWrapper}>
            <div className={styles.quizContent}>
                <div className={styles.questionPrompt}>
                    <p>Which scale degree sequence do you hear?</p>
                    <p className={styles.referenceNote}>
                        Scale: <strong>{currentQuestion.scaleType}</strong> | Reference Note: <strong>{currentQuestion.referenceNote}</strong>
                        {playReferenceNote && (
                            <button 
                                onClick={playReferenceNote} 
                                className={styles.playReferenceButton}
                                title="Play reference note"
                            >
                                ♪
                            </button>
                        )}
                    </p>
                </div>

                <div className={styles.answerOptionsPanel}>
                    <div className={styles.sequenceOptionsGrid}>
                        {currentQuestion.questionOptions.map((sequence, index) => (
                            <button
                                key={`${currentQuestion.referenceNote}-${currentQuestion.scaleType}-${index}`}
                                onClick={() => handleSequenceSelect(sequence)}
                                disabled={(correctAnswer !== null && !arraysEqual(correctAnswer, sequence)) || disabledOptions.some((seq) => arraysEqual(seq, sequence))}
                                className={
                                    correctAnswer && arraysEqual(correctAnswer, sequence)
                                        ? `${styles.sequenceOptionButton} ${styles.sequenceOptionButtonCorrect}`
                                        : disabledOptions.some((seq) => arraysEqual(seq, sequence))
                                        ? `${styles.sequenceOptionButton} ${styles.sequenceOptionButtonDisabled}`
                                        : styles.sequenceOptionButton
                                }
                            >
                                {formatScaleDegreeSequence(sequence)}
                            </button>
                        ))}
                    </div>
                </div>
                <div className={styles.feedbackContainer}>
                    {feedback && (
                        <span style={{ color: feedback.color }}>
                            {feedback.message}
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
}

function arraysEqual(a: number[], b: number[]): boolean {
    if (a.length !== b.length) return false;
    return a.every((val, index) => val === b[index]);
}
